import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import {
  SamsungDevice, samsungDiscoverTvs, samsungPairTv, samsungSetTvMode,
} from '../../services/samsungApi';

const ACCENT = '#2391FF';

type FoundTv = { host: string; name?: string; model?: string | null; mac?: string | null };

type Props = {
  device: SamsungDevice | null;
  open: boolean;
  onClose: () => void;
  onChanged: (device: SamsungDevice) => void;
};

const SamsungTvLanDialog: React.FC<Props> = ({ device, open, onClose, onChanged }) => {
  const { t } = useTranslation();
  const requestClose = useOverlayBackButton(open, onClose, 'samsung-tv-lan');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<FoundTv[]>([]);
  const [manualHost, setManualHost] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const detailOf = (e: any) => {
    const d = e?.response?.data?.detail;
    if (d && typeof d === 'object') return d.message || d.code;
    return d || e?.message || 'Error';
  };

  const choose = async (mode: 'cloud' | 'lan') => {
    if (!device) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const updated = await samsungSetTvMode(device.id, mode);
      onChanged(updated);
    } catch (e) {
      setError(String(detailOf(e)));
    } finally {
      setBusy(false);
    }
  };

  const search = async () => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const list = await samsungDiscoverTvs();
      setFound(list);
      if (!list.length) {
        setNote(t('settings.samsungDiscovery.tvLan.empty'));
      }
    } catch (e) {
      setError(String(detailOf(e)));
    } finally {
      setBusy(false);
    }
  };

  const pick = async (host: string, mac?: string | null) => {
    if (!device) return;
    setBusy(true);
    setError(null);
    setNote(t('settings.samsungDiscovery.tvLan.pairing'));
    try {
      if (device.control_mode !== 'lan') {
        const switched = await samsungSetTvMode(device.id, 'lan');
        onChanged(switched);
      }
      const updated = await samsungPairTv(device.id, host, mac);
      onChanged(updated);
      setNote(t('settings.samsungDiscovery.tvLan.paired'));
    } catch (e) {
      setError(String(detailOf(e)));
      setNote(null);
    } finally {
      setBusy(false);
    }
  };

  const mode = device?.control_mode === 'lan' ? 'lan' : 'cloud';

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : requestClose}
      PaperProps={{ sx: { bgcolor: '#1E1E1E', color: '#fff', width: '92vw', maxWidth: 480, m: 2 } }}
    >
      <DialogTitle sx={{ color: ACCENT, fontSize: '1rem', fontWeight: 700, pb: 0.5 }}>
        {device?.nombre || t('settings.samsungDiscovery.tvLan.title')}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: '#ccc', mb: 1.2 }}>
          {t('settings.samsungDiscovery.tvLan.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.2 }}>
          <Button
            fullWidth
            variant={mode === 'cloud' ? 'contained' : 'outlined'}
            disabled={busy}
            onClick={() => choose('cloud')}
            sx={{ bgcolor: mode === 'cloud' ? ACCENT : 'transparent', borderColor: ACCENT, color: mode === 'cloud' ? '#fff' : ACCENT }}
          >
            {t('settings.samsungDiscovery.tvLan.byCloud')}
          </Button>
          <Button
            fullWidth
            variant={mode === 'lan' ? 'contained' : 'outlined'}
            disabled={busy}
            onClick={() => choose('lan')}
            sx={{ bgcolor: mode === 'lan' ? ACCENT : 'transparent', borderColor: ACCENT, color: mode === 'lan' ? '#fff' : ACCENT }}
          >
            {t('settings.samsungDiscovery.tvLan.byLan')}
          </Button>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: '#bbb', mb: 1.2, lineHeight: 1.4 }}>
          {mode === 'lan'
            ? t('settings.samsungDiscovery.tvLan.lanHelp')
            : t('settings.samsungDiscovery.tvLan.cloudHelp')}
        </Typography>

        {mode === 'lan' && (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: ACCENT, mb: 0.6 }}>
              {t('settings.samsungDiscovery.tvLan.stepsTitle')}
            </Typography>
            {(['step1', 'step2', 'step3', 'step4'] as const).map((key, i) => (
              <Typography key={key} sx={{ fontSize: 12.5, color: '#ccc', mb: 0.45, lineHeight: 1.35 }}>
                {i + 1}. {t(`settings.samsungDiscovery.tvLan.${key}`)}
              </Typography>
            ))}
            {device?.lan_host && (
              <Typography sx={{ fontSize: 12, color: '#8bc34a', my: 0.8 }}>
                {device.lan_host}{device.lan_paired ? ' · OK' : ''}
              </Typography>
            )}
            <Button fullWidth variant="contained" disabled={busy} onClick={search} sx={{ bgcolor: ACCENT, my: 1 }}>
              {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('settings.samsungDiscovery.tvLan.search')}
            </Button>
            {found.map((tv) => (
              <Box key={tv.host} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, borderBottom: '1px solid #333' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13 }} noWrap>{tv.name || tv.host}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#888' }} noWrap>
                    {tv.host}{tv.model ? ` · ${tv.model}` : ''}
                  </Typography>
                </Box>
                <Button size="small" disabled={busy} onClick={() => pick(tv.host, tv.mac)} sx={{ color: ACCENT }}>
                  {t('settings.samsungDiscovery.tvLan.pick')}
                </Button>
              </Box>
            ))}
            <TextField
              fullWidth
              size="small"
              label={t('settings.samsungDiscovery.tvLan.ipLabel')}
              value={manualHost}
              onChange={(e) => setManualHost(e.target.value)}
              sx={{ mt: 1.2, input: { color: '#fff' }, label: { color: '#888' } }}
            />
            <Button
              fullWidth
              variant="outlined"
              disabled={busy || !manualHost.trim()}
              onClick={() => pick(manualHost.trim(), null)}
              sx={{ mt: 1, borderColor: ACCENT, color: ACCENT }}
            >
              {t('settings.samsungDiscovery.tvLan.pick')}
            </Button>
          </Box>
        )}

        {note && <Typography sx={{ fontSize: 12.5, color: '#8bc34a', mt: 1.2 }}>{note}</Typography>}
        {error && <Typography sx={{ fontSize: 12.5, color: '#e57373', mt: 1.2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={requestClose} disabled={busy} sx={{ color: '#ccc' }}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SamsungTvLanDialog;
